import React, { memo, useContext, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { Autocomplete, createFilterOptions } from '@material-ui/lab';
import { css } from '@emotion/react';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { Body, GreyBody, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';
import { greys } from '@/styles/colors';
import { useClientUser } from '@/hooks';
import Snackbar from '@/components/Snackbar';
import ServiceCatalogModal from '@/components/Account/ServiceCatalog/ServiceCatalogModal';
import { ModalContext } from '..';
import { TabTitle } from '../../../blocks';
import { isServiceCallbackOrder } from '../../../utilities/serviceCallback';
import { serviceCodeKey } from '../../../utilities/serviceLines';
import ServiceTimeInput from '@/components/ServiceTimeInput';
import { formatHHMM } from '@/utilities/hhmm';
import { serviceTypeLabel } from '@/constants/serviceTypes';
import { useAddServiceRow, useQuickAddService, useServicesPanel } from './hooks';
import { canQuickAddService } from './utils';

const filterOptions = createFilterOptions({
    matchFrom: 'any',
    ignoreCase: true,
    stringify: (option) => `${option.code} ${option.description}`,
});

const AddServiceRow = ({ order, onAdd, serviceCatalog, serviceCatalogError, serviceCatalogLoading }) => {
    const { options, selected, setSelected, inputValue, setInputValue, quantity, setQuantity, canAdd, handleAdd } =
        useAddServiceRow(order, onAdd, serviceCatalog);

    const catalogCaption = serviceCatalogError
        ? 'Unable to load available services.'
        : serviceCatalogLoading && !serviceCatalog
        ? 'Loading available services.'
        : 'Select an available service. Type a code or description to filter.';

    return (
        <Grid
            container
            spacing={2}
            css={css`
                margin-top: 16px;
                padding: 12px;
                border: 1px solid ${greys.border};
                border-radius: 4px;
            `}
        >
            <Grid item xs={12}>
                <Typography
                    variant="caption"
                    css={css`
                        color: ${greys.subtitle};
                    `}
                >
                    {catalogCaption}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={7}>
                <Autocomplete
                    autoHighlight
                    value={selected}
                    inputValue={inputValue}
                    options={options}
                    filterOptions={filterOptions}
                    noOptionsText={
                        serviceCatalogError
                            ? 'Services failed to load'
                            : serviceCatalogLoading && !serviceCatalog
                            ? 'Loading services'
                            : 'No matching services'
                    }
                    getOptionLabel={(option) => option?.code || ''}
                    renderOption={(option) => {
                        const showDescription = option.description && option.description !== option.code;
                        return (
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item xs={showDescription ? 4 : 10}>
                                    <strong>{option.code}</strong>
                                </Grid>
                                {showDescription ? (
                                    <Grid item xs={6}>
                                        <span
                                            css={css`
                                                color: ${greys.secondary};
                                            `}
                                        >
                                            {option.description}
                                        </span>
                                    </Grid>
                                ) : null}
                                <Grid
                                    item
                                    xs={2}
                                    css={css`
                                        text-align: right;
                                    `}
                                >
                                    <span
                                        css={css`
                                            color: ${greys.subtitle};
                                            font-size: 0.75rem;
                                        `}
                                    >
                                        {serviceTypeLabel(option.type)}
                                    </span>
                                </Grid>
                            </Grid>
                        );
                    }}
                    onChange={(_, value) => {
                        setSelected(value);
                        setInputValue(value?.code || '');
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                        setInputValue(newInputValue || '');
                        if (reason === 'input' && selected) {
                            setSelected(null);
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Code or description"
                            placeholder="Search service codes"
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12} sm={3}>
                <TextField
                    fullWidth
                    variant="outlined"
                    type="number"
                    label="Quantity"
                    value={quantity}
                    inputProps={{ min: 1, step: 1 }}
                    onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') return setQuantity('');
                        const parsed = Number(raw);
                        setQuantity(Number.isFinite(parsed) ? parsed : 1);
                    }}
                    onBlur={() => {
                        const q = Number(quantity);
                        setQuantity(Number.isFinite(q) && q > 0 ? Math.round(q) : 1);
                    }}
                />
            </Grid>
            <Grid
                item
                xs={12}
                sm={2}
                css={css`
                    display: flex;
                    align-items: flex-end;
                `}
            >
                <PrimaryButton disabled={!canAdd} onClick={handleAdd}>
                    Add Service
                </PrimaryButton>
            </Grid>
            {selected ? (
                <Grid item xs={12}>
                    <Typography
                        variant="caption"
                        css={css`
                            color: ${greys.subtitle};
                        `}
                    >
                        Selected: <strong>{selected.code}</strong>
                        {selected.description && selected.description !== selected.code
                            ? ` — ${selected.description}`
                            : ''}
                    </Typography>
                </Grid>
            ) : null}
        </Grid>
    );
};

// Services tab body — sole content of the SERVICES tab. Two display modes auto-detected
// from the order: service-callback (meta strip: appointment type + service time) and mixed
// delivery+services (accessorials). Add/edit/delete go through callbacks.addService /
// editServiceQuantity / deleteServiceGroup. Duplicate parser rows collapse into one row per
// code with summed quantity (editable via ± steppers; Delete removes the whole code-group).
// When `canQuickAdd`, a row whose code isn't in the catalog offers to add it (useQuickAddService).
const ServicesPanel = ({
    order,
    serviceCatalog,
    serviceCatalogError,
    serviceCatalogLoading,
    onDeleteGroup,
    onEditQuantity,
    onEditServiceTime,
    onAdd,
    canQuickAdd,
    catalogShipperId,
    onApplyCatalogService,
    onRefreshCatalogs,
}) => {
    const {
        isCallback,
        groupedLines,
        apptType,
        serviceTime,
        invalidCodes,
        canDelete,
        canEditQuantity,
        canEditServiceTime,
        canAdd,
        deleteDisabled,
        pendingDelete,
        setPendingDelete,
    } = useServicesPanel(order, { onDeleteGroup, onEditQuantity, onEditServiceTime, onAdd }, serviceCatalog);
    const quickAdd = useQuickAddService(order, catalogShipperId, {
        onApplyCatalogService,
        onRefreshCatalogs,
        enabled: canQuickAdd,
    });

    return (
        <>
            {/* Callback orders show appt type + service time as a meta strip; non-callback
                orders skip it (the TabTitle already labels the section). */}
            {isCallback ? (
                <Grid
                    container
                    css={css`
                        align-items: baseline;
                        justify-content: space-between;
                        margin-bottom: 8px;
                    `}
                >
                    <Grid item>
                        <Body>
                            <strong>{apptType}</strong>
                        </Body>
                    </Grid>
                    <Grid item>
                        {serviceTime != null ? (
                            <Body>
                                Service Time: <strong>{serviceTime} min</strong>
                            </Body>
                        ) : (
                            <GreyBody>Service Time: not set</GreyBody>
                        )}
                    </Grid>
                </Grid>
            ) : null}
            {isCallback ? (
                <Body
                    css={css`
                        display: block;
                        margin-bottom: 12px;
                    `}
                >
                    This work order has no physical products to deliver. The lines below are billable service items and
                    will be priced as accessorials.
                </Body>
            ) : null}
            {groupedLines.length === 0 ? (
                <Grid
                    container
                    css={css`
                        padding: 24px 12px;
                        border: 1px dashed ${greys.border};
                        border-radius: 4px;
                        justify-content: center;
                    `}
                >
                    <GreyBody>No services on this order yet.</GreyBody>
                </Grid>
            ) : (
                <Grid
                    container
                    direction="column"
                    css={css`
                        border: 1px solid ${greys.border};
                        border-radius: 4px;
                        overflow: hidden;
                    `}
                >
                    <Grid
                        container
                        css={css`
                            background: ${greys.selected};
                            padding: 8px 12px;
                            font-weight: 600;
                        `}
                    >
                        <Grid item xs={2}>
                            <Body>Code</Body>
                        </Grid>
                        <Grid item xs={canDelete ? 3 : 4}>
                            <Body>Description</Body>
                        </Grid>
                        <Grid item xs={2}>
                            <Body>Type</Body>
                        </Grid>
                        <Grid item xs={2}>
                            <Body>Qty</Body>
                        </Grid>
                        <Grid item xs={2}>
                            <Body>Service Time</Body>
                        </Grid>
                        {canDelete ? <Grid item xs={1} /> : null}
                    </Grid>
                    {groupedLines.map((group) => {
                        const codeInvalid = !!group.code && invalidCodes.has(serviceCodeKey(group.code));
                        return (
                            <Grid
                                key={group.service_id}
                                container
                                css={css`
                                    padding: 8px 12px;
                                    border-top: 1px solid ${greys.border};
                                    align-items: center;
                                `}
                            >
                                <Grid item xs={2}>
                                    {codeInvalid ? (
                                        <div
                                            css={css`
                                                display: flex;
                                                flex-direction: column;
                                                align-items: flex-start;
                                            `}
                                        >
                                            <Tooltip title={`"${group.code}" isn't a valid service for this order`}>
                                                <Body
                                                    css={css`
                                                        color: ${colors.reds.primary};
                                                        font-weight: 600;
                                                    `}
                                                >
                                                    {group.code}
                                                </Body>
                                            </Tooltip>
                                            {canQuickAdd ? (
                                                <Button
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => quickAdd.open(group)}
                                                >
                                                    Add to catalog
                                                </Button>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <Body>{group.code}</Body>
                                    )}
                                </Grid>
                                <Grid item xs={canDelete ? 3 : 4}>
                                    {/* Self-describing codes seed with description === code —
                                        render empty then so the column reads clean. Show the
                                        em-dash only for genuinely missing descriptions. */}
                                    <GreyBody>
                                        {group.description
                                            ? group.description === group.code
                                                ? ''
                                                : group.description
                                            : '—'}
                                    </GreyBody>
                                </Grid>
                                <Grid item xs={2}>
                                    <GreyBody>{serviceTypeLabel(group.type) || '—'}</GreyBody>
                                </Grid>
                                <Grid item xs={2}>
                                    <div
                                        css={css`
                                            display: flex;
                                            align-items: center;
                                            gap: 6px;
                                        `}
                                    >
                                        <Body>{group.quantity}</Body>
                                        {canEditQuantity ? (
                                            <div
                                                css={css`
                                                    display: flex;
                                                    flex-direction: column;
                                                `}
                                            >
                                                <Tooltip title="Add one">
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`Increase ${group.code} quantity`}
                                                        onClick={() => onEditQuantity(group.code, 1)}
                                                        css={css`
                                                            padding: 0;
                                                            height: 16px;
                                                            width: 20px;
                                                            border-radius: 4px;
                                                        `}
                                                    >
                                                        <KeyboardArrowUpIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={group.quantity <= 1 ? '' : 'Remove one'}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Decrease ${group.code} quantity`}
                                                            disabled={group.quantity <= 1}
                                                            onClick={() => onEditQuantity(group.code, -1)}
                                                            css={css`
                                                                padding: 0;
                                                                height: 16px;
                                                                width: 20px;
                                                                border-radius: 4px;
                                                            `}
                                                        >
                                                            <KeyboardArrowDownIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        ) : null}
                                    </div>
                                </Grid>
                                <Grid item xs={2}>
                                    {canEditServiceTime ? (
                                        <ServiceTimeInput
                                            value={group.service_time}
                                            onCommit={(next) => onEditServiceTime(group.code, next)}
                                            ariaLabel={`${group.code} service time`}
                                            width={80}
                                        />
                                    ) : (
                                        <GreyBody>
                                            {Number.isFinite(group.service_time) ? formatHHMM(group.service_time) : '—'}
                                        </GreyBody>
                                    )}
                                </Grid>
                                {canDelete ? (
                                    <Grid
                                        item
                                        xs={1}
                                        css={css`
                                            text-align: right;
                                        `}
                                    >
                                        <Tooltip
                                            title={
                                                deleteDisabled
                                                    ? 'Service callback orders need at least one service line'
                                                    : 'Delete'
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    disabled={deleteDisabled}
                                                    onClick={() => setPendingDelete(group)}
                                                    css={css`
                                                        color: ${colors.reds.primary};
                                                        border-radius: 4px;
                                                        padding: 6px;
                                                    `}
                                                >
                                                    <DeleteOutlinedIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Grid>
                                ) : null}
                            </Grid>
                        );
                    })}
                </Grid>
            )}
            {canAdd ? (
                <AddServiceRow
                    order={order}
                    serviceCatalog={serviceCatalog}
                    serviceCatalogError={serviceCatalogError}
                    serviceCatalogLoading={serviceCatalogLoading}
                    onAdd={onAdd}
                />
            ) : null}
            {/* Delete confirmation. The grouped row folds every duplicate of a code into one
                line, so deleting it removes all of them — spell out the count + code. */}
            <Dialog open={!!pendingDelete} onClose={() => setPendingDelete(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete service?</DialogTitle>
                <DialogContent>
                    <Body>
                        {pendingDelete
                            ? `This will delete ${
                                  pendingDelete.quantity === 1 ? 'this' : `all ${pendingDelete.quantity}`
                              } "${pendingDelete.code}" service${
                                  pendingDelete.quantity === 1 ? '' : 's'
                              } from this order.`
                            : ''}
                    </Body>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={() => setPendingDelete(null)}>Cancel</SecondaryButton>
                    <PrimaryButton
                        onClick={() => {
                            onDeleteGroup(pendingDelete.code);
                            setPendingDelete(null);
                        }}
                    >
                        Delete
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
            {canQuickAdd ? (
                <>
                    <ServiceCatalogModal
                        open={!!quickAdd.pending}
                        service={null}
                        defaults={quickAdd.pending?.defaults}
                        dimensionFields={quickAdd.pending?.dimensionFields}
                        codeLocked
                        subtitle="Saves to your service catalog now, not when you save the order."
                        onClose={quickAdd.close}
                        onSave={quickAdd.save}
                        loading={quickAdd.saving}
                        serviceRate={quickAdd.serviceRate}
                        serviceRates={quickAdd.serviceRates}
                    />
                    <Snackbar
                        open={!!quickAdd.notification.message}
                        handleClose={quickAdd.clearNotification}
                        {...quickAdd.notification}
                    />
                </>
            ) : null}
        </>
    );
};

const ServicesTab = memo(() => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { order, serviceCatalog, serviceCatalogError, serviceCatalogLoading, serviceCatalogShipperId, options } =
        modalState;
    const { accountType, client_id, userType, teammateRoles } = useClientUser();
    const [collapsed, setCollapsed] = useState(false);

    const isCallback = isServiceCallbackOrder(order);
    const canQuickAdd =
        typeof callbacks.applyCatalogService === 'function' &&
        typeof callbacks.refreshServiceCatalogs === 'function' &&
        canQuickAddService({
            accountType,
            clientId: client_id,
            shipperId: serviceCatalogShipperId,
            readOnly: options?.readOnly,
            userType,
            teammateRoles,
        });

    return (
        <>
            <TabTitle collapsed={collapsed} callbacks={{ setCollapsed }}>
                {isCallback ? 'Service Callback' : 'Services'}
            </TabTitle>
            {collapsed ? null : (
                <ServicesPanel
                    order={order}
                    serviceCatalog={serviceCatalog}
                    serviceCatalogError={serviceCatalogError}
                    serviceCatalogLoading={serviceCatalogLoading}
                    onDeleteGroup={callbacks.deleteServiceGroup}
                    onEditQuantity={callbacks.editServiceQuantity}
                    onEditServiceTime={callbacks.editServiceTime}
                    onAdd={callbacks.addService}
                    canQuickAdd={canQuickAdd}
                    catalogShipperId={serviceCatalogShipperId}
                    onApplyCatalogService={callbacks.applyCatalogService}
                    onRefreshCatalogs={callbacks.refreshServiceCatalogs}
                />
            )}
        </>
    );
});

export default ServicesTab;
